import React, { useCallback, useMemo, useState } from 'react';
import { Card, CardBody } from '@spark/ui-components/components/Card';
import { notification } from '@spark/ui-components/utils/notification';
import ConfirmDeleteModal from '@/containers/UI/Modals/index2';
import { downloadAnexosDepotsZip, removeAnexoAcompanhamento } from '@spark/services';
import { AnexosHeader, AnexosTable, AnexosFilter } from './';
import { useAnexosListagem } from '../../../hooks';

const toDigitsOnly = (value) => (value ? value.replace(/\D/g, '') : '');

const AnexosCard = () => {
  const [exportandoZip, setExportandoZip] = useState(false);
  const [selectedAnexos, setSelectedAnexos] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [anexoParaExcluir, setAnexoParaExcluir] = useState(null);
  const [deletingAnexo, setDeletingAnexo] = useState(false);
  const {
    data,
    loading,
    filterLoading,
    totalRegistros,
    paginationState,
    handlePaginationChange,
    handleFilterApply,
    handleLimparFiltros,
    fetchData,
  } = useAnexosListagem();

  const selectedIds = useMemo(() => selectedAnexos.map((item) => item.id), [selectedAnexos]);

  const cnpjSelecionado = useMemo(
    () => (selectedAnexos.length ? toDigitsOnly(selectedAnexos[0].cnpjCliente || '') : ''),
    [selectedAnexos]
  );

  const pageSelectableAnexos = useMemo(() => {
    if (!data?.length) return [];
    if (!cnpjSelecionado) return data;
    return data.filter((item) => toDigitsOnly(item.cnpjCliente || '') === cnpjSelecionado);
  }, [data, cnpjSelecionado]);

  const allPageSelected = useMemo(
    () => pageSelectableAnexos.length > 0 && pageSelectableAnexos.every((item) => selectedIds.includes(item.id)),
    [pageSelectableAnexos, selectedIds]
  );

  const selectAllDisabled = useMemo(
    () => !data?.length || (cnpjSelecionado && pageSelectableAnexos.length === 0),
    [data, cnpjSelecionado, pageSelectableAnexos]
  );

  const handleToggleSelectAnexo = useCallback((row) => {
    if (!row) return;

    setSelectedAnexos((prev) => {
      const exists = prev.some((item) => item.id === row.id);
      if (exists) return prev.filter((item) => item.id !== row.id);
      return [...prev, row];
    });
  }, []);

  const handleToggleSelectAll = useCallback(() => {
    if (!data?.length) return;

    setSelectedAnexos((prev) => {
      const todasSelecionadas = data.every((item) => prev.some((nota) => nota.id === item.id));
      if (todasSelecionadas) {
        return prev.filter((nota) => !data.some((item) => item.id === nota.id));
      }

      const novas = data.filter((item) => !prev.some((nota) => nota.id === item.id));
      return [...prev, ...novas];
    });
  }, [data]);

  const handleExcluirAnexo = useCallback((row) => {
    if (!row?.id) return;
    setAnexoParaExcluir(row);
    setShowDeleteModal(true);
  }, []);

  const handleConfirmarExclusaoAnexo = useCallback(async () => {
    const anexoId = anexoParaExcluir?.id;
    if (!anexoId) return;

    try {
      setDeletingAnexo(true);
      await removeAnexoAcompanhamento(anexoId);
      notification.success('Anexo excluido com sucesso.');
      setSelectedAnexos((prev) => prev.filter((item) => item.id !== anexoId));
      setShowDeleteModal(false);
      setAnexoParaExcluir(null);
      await fetchData();
    } catch (error) {
      console.error('Erro ao excluir anexo:', error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.mensagem ||
        error?.message ||
        'Erro ao excluir anexo.';
      notification.error(message);
    } finally {
      setDeletingAnexo(false);
    }
  }, [anexoParaExcluir, fetchData]);

  const handleFecharModalExclusao = useCallback(() => {
    setShowDeleteModal(false);
    setAnexoParaExcluir(null);
  }, []);

  const handleExportarZip = useCallback(async () => {
    if (!selectedIds.length) {
      notification.warning('Selecione ao menos um anexo para exportar.');
      return;
    }

    try {
      setExportandoZip(true);
      const blobResponse = await downloadAnexosDepotsZip(selectedIds);
      const blob =
        blobResponse instanceof Blob
          ? blobResponse
          : new Blob([blobResponse], { type: 'application/zip' });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `anexos-depots-${Date.now()}.zip`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao exportar anexos:', error);
      notification.error('Erro ao exportar anexos.');
    } finally {
      setExportandoZip(false);
    }
  }, [selectedIds]);

  return (
    <>
      <Card>
        <CardBody>
          <AnexosHeader
            totalRegistros={totalRegistros}
            selectedCount={selectedAnexos.length}
            onExportarPendencias={handleExportarZip}
            exportarPendenciasLoading={exportandoZip}
          />

          <AnexosFilter
            loading={filterLoading}
            onApply={handleFilterApply}
            onLimpar={handleLimparFiltros}
          />

          <AnexosTable
            data={data}
            loading={loading}
            totalRegistros={totalRegistros}
            paginationState={paginationState}
            setPaginationState={handlePaginationChange}
            onExcluir={handleExcluirAnexo}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelectAnexo}
            onToggleSelectAll={handleToggleSelectAll}
            allPageSelected={allPageSelected}
            selectAllDisabled={selectAllDisabled}
          />
        </CardBody>
      </Card>

      <ConfirmDeleteModal
        show={showDeleteModal}
        onClose={handleFecharModalExclusao}
        onConfirm={handleConfirmarExclusaoAnexo}
        loading={deletingAnexo}
        title="Excluir Anexo"
        message={`Tem certeza que deseja excluir o anexo${anexoParaExcluir?.nomeOriginal ? ` "${anexoParaExcluir.nomeOriginal}"` : ''}?`}
      />
    </>
  );
};

export default AnexosCard;
